import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCourseConfig, selectGroupSessions } from '../../store/appDataStore';
import { selectDataRefreshNonce, selectUserInfos } from '../../store/userDataStore';
import { useAuth } from '../../providers/AuthProvider';
import { chartsService, ChartSeriesResponse } from '../../services/chartsService';
import { tdGroupService } from '../../services/tdGroupService';
import { userService } from '../../services/userService';
import { toISOString } from '../../misc/dateFormat';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart } from 'chart.js';
import 'chartjs-adapter-date-fns';

Chart.register(annotationPlugin);

const INDICATORS = [
  { key: 'lint_errors', label: 'Lint (Ruff)', cumulative: false },
  { key: 'mypy_errors', label: 'MyPy', cumulative: false },
  { key: 'html_errors', label: 'HTML', cumulative: false },
  { key: 'css_errors', label: 'CSS', cumulative: false },
  { key: 'user_stories_points', label: 'Burnup', cumulative: true },
];

const ChartsPage: React.FC = () => {
  const groupSessions = useSelector(selectGroupSessions);
  const userInfos = useSelector(selectUserInfos);
  const courseConfig = useSelector(selectCourseConfig);
  const refreshNonce = useSelector(selectDataRefreshNonce);
  const { getValidToken } = useAuth();

  // Détection du rôle (enseignant ou étudiant)
  const isTeacher = userInfos?.access === 'teacher' || userInfos?.access === 'admin';

  // Sélection des indicateurs à afficher
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>([INDICATORS[0].key]);
  const [selectedCycles, setSelectedCycles] = useState<number[]>(courseConfig?.cycles?.map((c: any) => c.id) || []);
  
  // Filtres enseignant uniquement
  const [selectedTdGroup, setSelectedTdGroup] = useState<string>('');
  const [selectedProjectGroup, setSelectedProjectGroup] = useState<string>('');

  // Listes pour les filtres enseignant : groupes TD et binômes (plus déduits des résultats, qui ne
  // sont plus téléchargés)
  const [uniqueTdGroups, setUniqueTdGroups] = useState<string[]>([]);
  const [uniqueProjectGroups, setUniqueProjectGroups] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (!isTeacher) return;
    (async () => {
      try {
        const token = await getValidToken();
        if (!token) return;
        const [tdGroups, projectGroups] = await Promise.all([tdGroupService.list(token), userService.getProjectGroups(token)]);
        setUniqueTdGroups(tdGroups.map(g => g.code));
        setUniqueProjectGroups(projectGroups
          .filter((g: any) => g.name)
          .map((g: any) => ({ id: g.id, name: g.name }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name)));
      } catch (e) {
        console.error('Erreur chargement des filtres Charts :', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  // Courbes calculées par le serveur (GET /charts/series, en cache côté API) : une petite requête
  // par changement de filtre au lieu de télécharger et recalculer tous les résultats CI
  const [chartResponse, setChartResponse] = useState<ChartSeriesResponse | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);
  useEffect(() => {
    if (selectedCycles.length === 0 || selectedIndicators.length === 0) {
      setChartResponse(null);
      return;
    }
    const controller = new AbortController();
    (async () => {
      setChartLoading(true);
      setChartError(null);
      try {
        const token = await getValidToken();
        if (!token) throw new Error('Session expirée, reconnectez-vous.');
        setChartResponse(await chartsService.getSeries({
          cycles: selectedCycles, indicators: selectedIndicators,
          tdGroup: isTeacher ? selectedTdGroup : undefined,
          projectGroupId: isTeacher ? selectedProjectGroup : undefined,
        }, token, controller.signal));
      } catch (e: any) {
        if (e.name !== 'AbortError') setChartError(e.message || 'Erreur lors du chargement des courbes');
      } finally {
        if (!controller.signal.aborted) setChartLoading(false);
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCycles, selectedIndicators, selectedTdGroup, selectedProjectGroup, isTeacher, refreshNonce]);

  // Première et dernière date de résultat des courbes affichées (bornes de l'axe par défaut)
  const allDates = [chartResponse?.dates.min, chartResponse?.dates.max].filter((d): d is string => !!d);

  // Déterminer la date de départ (min) et la date de fin (max)
  // Pour les étudiants : utiliser les sessions du groupe TD
  // Pour les enseignants : utiliser les dates de résultats
  let minDate: string | undefined = undefined;
  if (!isTeacher && userInfos && groupSessions && userInfos.td_group && groupSessions[userInfos.td_group]) {
    const sessionObj = groupSessions[userInfos.td_group];
    let sessionDates: string[] = [];
    if (sessionObj && typeof sessionObj === 'object' && 'sessions' in sessionObj && Array.isArray(sessionObj.sessions)) {
      sessionDates = sessionObj.sessions;
    } else if (Array.isArray(sessionObj)) {
      sessionDates = sessionObj;
    }
    if (sessionDates.length > 0) {
      minDate = sessionDates[0];
    }
  }
  
  // Déterminer la date max : la dernière session du groupe TD (étudiant) ou la dernière date de résultat
  let maxDate: string | undefined = undefined;
  if (!isTeacher && userInfos && groupSessions && userInfos.td_group && groupSessions[userInfos.td_group]) {
    const sessionObj = groupSessions[userInfos.td_group];
    let sessionDates: string[] = [];
    if (sessionObj && typeof sessionObj === 'object' && 'sessions' in sessionObj && Array.isArray(sessionObj.sessions)) {
      sessionDates = sessionObj.sessions;
    } else if (Array.isArray(sessionObj)) {
      sessionDates = sessionObj;
    }
    if (sessionDates.length > 0) {
      const lastDate = new Date(sessionDates[sessionDates.length - 1]);
      lastDate.setDate(lastDate.getDate() + 1);
      maxDate = toISOString(lastDate);
    }
  }
  
  // Fallback pour tous : si pas de session, utiliser la dernière date de résultat + 1 jour
  if (!maxDate && allDates.length > 0) {
    const lastDate = new Date(allDates[allDates.length - 1]);
    lastDate.setDate(lastDate.getDate() + 1);
    maxDate = toISOString(lastDate);
  }
  
  // Fallback pour minDate : si pas défini, utiliser la première date de résultat
  if (!minDate && allDates.length > 0) {
    minDate = allDates[0];
  }

  // Un seul graphique avec toutes les courbes sélectionnées (une par indicateur)
  // Pour chaque date, chaque dataset représente un indicateur, cumulant sur les cycles sélectionnés
  const chartColors = [
    'rgba(75,192,192,1)',
    'rgba(255,99,132,1)',
    'rgba(54,162,235,1)',
    'rgba(255,206,86,1)',
    'rgba(153,102,255,1)',
    'rgba(255,159,64,1)',
    'rgba(0,200,83,1)'
  ];

  // Pour l'axe x à l'échelle des dates, on utilise 'time' de chart.js
  // Il faut que labels et data utilisent des objets Date ou des chaînes ISO
  // Correction : on conserve les dates d'origine pour l'indexation des données

  // Préparer les points {x, y} pour tous les datasets
  // On ne fournit plus labels, mais chaque dataset a ses propres x/y
  const projectColors = [
    'rgba(100, 150, 200, 1)',
    'rgba(200, 100, 150, 1)',
    'rgba(150, 200, 100, 1)',
    'rgba(100, 200, 150, 1)',
    'rgba(200, 150, 100, 1)',
    'rgba(150, 100, 200, 1)'
  ];
  const projectColorIndex = new Map<number, number>();

  // Courbes du serveur -> datasets Chart.js (libellés et couleurs comme avant la refonte)
  let indicatorDatasets: any[] = (chartResponse?.series || []).map((serie) => {
    const indicatorIdx = INDICATORS.findIndex(i => i.key === serie.indicator);
    const indicatorLabel = INDICATORS[indicatorIdx]?.label || serie.indicator;
    if (serie.kind === 'project') {
      if (!projectColorIndex.has(serie.project_group_id!)) projectColorIndex.set(serie.project_group_id!, projectColorIndex.size);
      const color = projectColors[projectColorIndex.get(serie.project_group_id!)! % projectColors.length];
      return {
        label: `${serie.name} - ${indicatorLabel}`,
        data: serie.points,
        borderColor: color,
        backgroundColor: color.replace('1)', '0.2)'),
        tension: 0.2,
        borderWidth: 1,
      };
    }
    if (serie.kind === 'mean' && isTeacher && selectedTdGroup) {
      return {
        label: `MOYENNE ${selectedTdGroup} - ${indicatorLabel}`,
        data: serie.points,
        borderColor: 'rgba(0, 0, 0, 1)',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        tension: 0.2,
        borderWidth: 3,
        fill: false,
      };
    }
    const color = chartColors[indicatorIdx % chartColors.length];
    return {
      label: indicatorLabel,
      data: serie.points,
      borderColor: color,
      backgroundColor: color.replace('1)', '0.2)'),
      tension: 0.2,
    };
  });

  // Ajout de la ligne d'objectif burnup si burnup sélectionné
  if (selectedIndicators.includes('user_stories_points') && minDate && maxDate) {
    // Somme des story points de tous les cycles sélectionnés
    let maxBurnup = 0;
    if (courseConfig && courseConfig.cycles) {
      courseConfig.cycles.forEach((cycle: any) => {
        if (selectedCycles.includes(cycle.id)) {
          maxBurnup += (cycle.user_stories || []).reduce((acc: number, us: any) => acc + (us.story_points || 0), 0);
        }
      });
    }
    // Calculer la date d'objectif : maxDate - 0,5 jour (midi)
    let objectifDate = maxDate;
    if (objectifDate) {
      const d = new Date(objectifDate);
      d.setHours(d.getHours() - 12);
      objectifDate = toISOString(d);
    }
    indicatorDatasets = [
      ...indicatorDatasets,
      {
        label: 'Objectif',
        data: [
          { x: toISOString(minDate), y: 0 },
          { x: objectifDate, y: maxBurnup }
        ],
        borderColor: 'black',
        borderWidth: 2,
        pointRadius: 0,
        borderDash: [4, 4],
        fill: false,
        tension: 0,
      }
    ];
  }

  // Calculer la valeur max Y pour placer dynamiquement les labels d'annotations
  let yMax = 1;
  if (selectedIndicators.length > 0) {
    const allY = indicatorDatasets.flatMap(ds => (ds.data as any[] || []).map(pt => pt.y)).filter(y => typeof y === 'number');
    if (allY.length > 0) yMax = Math.max(...allY);
  }

  // Générer les lignes de session (annotations Chart.js)
  // UNIQUEMENT POUR LES ÉTUDIANTS
  let sessionAnnotations: any = {};
  if (!isTeacher && userInfos && groupSessions) {
    if (userInfos.td_group && groupSessions[userInfos.td_group]) {
      const sessionObj = groupSessions[userInfos.td_group];
      let sessionDates: string[] = [];
      if (sessionObj && typeof sessionObj === 'object' && 'sessions' in sessionObj && Array.isArray(sessionObj.sessions)) {
        sessionDates = sessionObj.sessions;
      } else if (Array.isArray(sessionObj)) {
        sessionDates = sessionObj;
      }
      // Grouper les sessions par date complète (clé = date ISO à midi)
      const dateMap: Record<string, number[]> = {};
      sessionDates.forEach((dateStr, idx) => {
        const d = new Date(dateStr);
        d.setHours(12, 0, 0, 0);
        const key = toISOString(d); // clé = date complète à midi
        if (!dateMap[key]) dateMap[key] = [];
        dateMap[key].push(idx + 1); // session number (1-based)
      });
      // Utiliser yMax déjà calculé plus haut
      Object.entries(dateMap).forEach(([dateKey, sessionNums]) => {
        const x = dateKey;
        sessionAnnotations[`sessionLine${sessionNums.join('_')}`] = {
          type: 'line',
          xMin: x,
          xMax: x,
          borderColor: '#888',
          borderWidth: 2,
          borderDash: [2, 2],
          display: true,
          z: 10,
          label: {
            display: true,
            content: sessionNums.length > 1 ? `Sessions ${sessionNums.join(' & ')}` : `Session ${sessionNums[0]}`,
            position: 'end',
            rotation: -90,
            color: '#fff',
            backgroundColor: '#444',
            font: { size: 12, weight: 'bold' },
            borderRadius: 4,
            padding: 6,
            borderWidth: 1,
            borderColor: '#ccc',
          },
        };
      });
    }
  }

  // Fonction pour convertir une date ISO en pourcentage gauche (pour overlay)

  // Préparer les datasets pour Chart.js
  const chartData = {
    datasets: indicatorDatasets
  };

  // Options Chart.js
  const chartOptions: any = {
    responsive: true,
    plugins: {
      legend: { display: true },
      annotation: {
        annotations: sessionAnnotations
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      x: {
        type: 'time',
        time: { unit: 'day' },
  // title supprimé
        ...(maxDate ? { max: maxDate } : {}),
        ticks: {
          callback: (value: any) => {
            const date = new Date(value);
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
          }
        }
      },
      y: {
        beginAtZero: true,
  // title supprimé
  suggestedMax: yMax * 1.25
      }
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: 400 }}>
      {/* Conteneur global des filtres sur une seule ligne */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 16, alignItems: 'stretch' }}>
        
        {/* Bloc des filtres communs à gauche */}
        <div style={{ flex: 1 }}>
          {/* Sélecteur de cycles */}
          {courseConfig?.cycles && (
            <div style={{ marginBottom: 16 }}>
              <strong>Cycles :</strong>
              {courseConfig.cycles.map((cycle: any) => (
                <label key={cycle.id} style={{ marginRight: 12, marginLeft: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedCycles.includes(cycle.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        setSelectedCycles([...selectedCycles, cycle.id]);
                      } else {
                        setSelectedCycles(selectedCycles.filter(id => id !== cycle.id));
                      }
                    }}
                  />
                  {cycle.name || `Cycle ${cycle.id}`}
                </label>
              ))}
            </div>
          )}
          
          {/* Sélecteur d'indicateurs */}
          <div style={{ marginBottom: 16 }}>
            <strong>Indicateurs :</strong>
            {INDICATORS.map(ind => (
              <label key={ind.key} style={{ marginRight: 12, marginLeft: 8 }}>
                <input
                  type="checkbox"
                  checked={selectedIndicators.includes(ind.key)}
                  onChange={e => {
                    if (e.target.checked) {
                      setSelectedIndicators([...selectedIndicators, ind.key]);
                    } else {
                      setSelectedIndicators(selectedIndicators.filter(k => k !== ind.key));
                    }
                  }}
                />
                {ind.label}
              </label>
            ))}
          </div>
        </div>
        
        {/* Bloc des filtres enseignant à droite */}
        {isTeacher && (
          <div style={{ flex: 1, borderRadius: 4 }}>
            {/* Filtre Groupe TD */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ marginRight: 12 }}>
                Groupe TD :
                <select
                  value={selectedTdGroup}
                  onChange={e => {
                    setSelectedTdGroup(e.target.value);
                    // Réinitialiser le groupe projet si on sélectionne un TD
                    if (e.target.value) {
                      setSelectedProjectGroup('');
                    }
                  }}
                  style={{ marginLeft: 8, padding: 4 }}
                >
                  <option value="">-- Tous les groupes TD --</option>
                  {uniqueTdGroups.map(tdGroup => (
                    <option key={tdGroup} value={tdGroup}>{tdGroup}</option>
                  ))}
                </select>
              </label>
            </div>

            {/* Filtre Groupe Projet */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ marginRight: 12 }}>
                Groupe Projet :
                <select
                  value={selectedProjectGroup}
                  onChange={e => {
                    setSelectedProjectGroup(e.target.value);
                    // Réinitialiser le groupe TD si on sélectionne un projet
                    if (e.target.value) {
                      setSelectedTdGroup('');
                    }
                  }}
                  style={{ marginLeft: 8, padding: 4 }}
                  disabled={selectedTdGroup !== ''}
                >
                  <option value="">-- Tous les projets --</option>
                  {uniqueProjectGroups.map(proj => (
                    <option key={proj.id} value={proj.id}>{proj.name}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        )}
      </div>
      
      {/* Conteneur du graphique avec message informatif positionné en haut à gauche */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        {/* Message informatif selon le contexte - positionné en haut à gauche */}
        {isTeacher && (
          <div style={{
            position: 'absolute',
            top: selectedTdGroup ? '12%' : '4%',
            left: '2%',
            padding: 8,
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #2196f3',
            borderRadius: 4,
            fontSize: '0.9em',
            maxWidth: '400px',
            zIndex: 10
          }}>
            {selectedProjectGroup ? (
              <p style={{ margin: 0 }}>📊 <strong>Vue d'un projet :</strong> Affichage de tous les résultats du projet sélectionné.</p>
            ) : selectedTdGroup ? (
              <p style={{ margin: 0 }}>📊 <strong>Vue d'un groupe TD :</strong> Affichage des courbes de tous les projets du groupe TD (en couleurs) + moyenne du groupe (en noir)</p>
            ) : (
              <p style={{ margin: 0 }}>📊 <strong>Vue globale :</strong> Affichage de la courbe moyenne de tous les projets des cycles sélectionnés.</p>
            )}
          </div>
        )}
        
        {chartError && <p style={{ color: '#c62828' }}>{chartError}</p>}
        {chartLoading && !chartResponse && <p>Chargement des courbes…</p>}
        {/* Graphique Chart.js avec labels natifs d'annotation */}
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
// ...existing code...
}

export default ChartsPage;
